/**
 * new-hmp-design-banner-slideshow.js
 * ---------------------------------------------------------------
 * Turns a "New HMP: Image Banner" section into a slideshow:
 * one large banner, the next one peeking in on the right,
 * arrows to move, swipe on mobile.
 *
 * It only runs on sections that carry the class `homepureslideshow`
 * (the section's "Show banners as a slideshow" checkbox adds it).
 * Remove the class and the section goes back to stacked banners.
 *
 * How it works: the browser does the scrolling and the snapping
 * (CSS scroll-snap), so swipe, trackpad and keyboard focus all work
 * natively. This file only adds the arrows, the "current slide"
 * state, optional autoplay and the screen reader labels.
 */
(function () {
  'use strict';

  // Loaded twice on the same page (two sections)? Just re-scan.
  if (window.__homepureSlideshow) {
    window.__homepureSlideshow.scan(document);
    return;
  }

  var ROOT = '.homepureslideshow';
  var SLIDE = 'new-hmp-design-banner';
  var instances = new WeakMap();
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  var ICON = {
    prev: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    pause: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M8 5v14M16 5v14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>',
    play: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M8 5l11 7-11 7z" fill="currentColor"/></svg>'
  };

  function makeButton(modifier, label, icon) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'hp-slides__btn hp-slides__btn--' + modifier;
    b.setAttribute('aria-label', label);
    b.innerHTML = icon;
    return b;
  }

  function Slideshow(root) {
    var viewport = root.querySelector('[data-hp-viewport]');
    var track = root.querySelector('[data-hp-track]');
    if (!viewport || !track) { return null; }
    var stage = root.querySelector('[data-hp-stage]') || viewport.parentNode;

    var slides = [];
    var index = 0;
    var timer = null;
    var rafId = 0;
    var lockUntil = 0;       // ignore scroll-based index updates while we animate
    var syncTimer = null;
    var hovering = false;
    var focusing = false;
    var inView = true;
    var userStopped = false;
    var evictions = 0;
    var autoplayWanted = root.getAttribute('data-autoplay') === 'true';
    var interval = Math.max(3000, parseInt(root.getAttribute('data-interval'), 10) || 5000);
    var undo = [];           // cleanup functions

    var controls = null;
    var prevBtn = null;
    var nextBtn = null;
    var pauseBtn = null;

    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      undo.push(function () { target.removeEventListener(type, fn, opts); });
    }

    // ---- slides --------------------------------------------------
    function collect() {
      slides = Array.prototype.filter.call(track.children, function (el) {
        return el.classList && el.classList.contains(SLIDE);
      });
      slides.forEach(function (s, i) {
        s.setAttribute('role', 'group');
        s.setAttribute('aria-roledescription', 'slide');
        s.setAttribute('aria-label', (i + 1) + ' of ' + slides.length);
      });
    }

    // Anything that is not a banner (for example something another script
    // drops into the section) must not become a slide. Move it out of the
    // slideshow, to just below it, so it still shows on the page.
    function evictStrangers() {
      Array.prototype.slice.call(track.children).forEach(function (el) {
        if (!el.classList.contains(SLIDE)) {
          root.appendChild(el);
          evictions += 1;
        }
      });
    }

    function maxScroll() {
      return viewport.scrollWidth - viewport.clientWidth;
    }

    function nearestIndex() {
      if (viewport.scrollLeft >= maxScroll() - 2) { return slides.length - 1; }
      var best = 0;
      var bestDist = Infinity;
      var x = viewport.scrollLeft;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slides[i].offsetLeft - x);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    function setDisabled(btn, disabled) {
      if (btn) { btn.setAttribute('aria-disabled', disabled ? 'true' : 'false'); }
    }

    function update() {
      slides.forEach(function (s, i) {
        if (i === index) { s.setAttribute('data-current', 'true'); }
        else { s.removeAttribute('data-current'); }
      });
      setDisabled(prevBtn, index <= 0);
      setDisabled(nextBtn, index >= slides.length - 1);
    }

    function goTo(i, instant) {
      if (!slides.length) { return; }
      i = Math.max(0, Math.min(slides.length - 1, i));
      var left = Math.min(slides[i].offsetLeft, Math.max(0, maxScroll()));
      var smooth = !instant && !reduced.matches;
      index = i;
      lockUntil = Date.now() + (smooth ? 700 : 0);
      viewport.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
      update();
      clearTimeout(syncTimer);
      syncTimer = setTimeout(function () {
        index = nearestIndex();
        update();
      }, smooth ? 750 : 50);
    }

    // ---- images --------------------------------------------------
    // Banners after the first are lazy-loaded. Fetch them once the page
    // is idle (or the shopper touches the slideshow) so a quick arrow
    // click never lands on an empty slide.
    function loadAllImages() {
      slides.forEach(function (s) {
        Array.prototype.forEach.call(s.querySelectorAll('img[loading="lazy"]'), function (img) {
          img.loading = 'eager';
        });
      });
    }

    // ---- autoplay ------------------------------------------------
    function canAutoplay() {
      return autoplayWanted && !reduced.matches && !userStopped &&
        !hovering && !focusing && inView && !document.hidden && slides.length > 1;
    }

    function stopTimer() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function applyAutoplay() {
      stopTimer();
      if (canAutoplay()) {
        timer = setInterval(function () {
          goTo(index >= slides.length - 1 ? 0 : index + 1);
        }, interval);
      }
    }

    function syncPauseButton() {
      if (!pauseBtn) { return; }
      pauseBtn.setAttribute('aria-label', userStopped ? 'Play banner rotation' : 'Pause banner rotation');
      pauseBtn.innerHTML = userStopped ? ICON.play : ICON.pause;
    }

    // A manual action (arrow, swipe, tap) hands control to the shopper.
    function handedOver() {
      loadAllImages();
      if (autoplayWanted && !userStopped) {
        userStopped = true;
        stopTimer();
        syncPauseButton();
      }
    }

    // ---- build ---------------------------------------------------
    collect();
    if (slides.length < 2) { return null; }

    root.setAttribute('role', 'region');
    root.setAttribute('aria-roledescription', 'carousel');
    if (!root.getAttribute('aria-label')) { root.setAttribute('aria-label', 'Featured banners'); }

    controls = document.createElement('div');
    controls.className = 'hp-slides__controls';
    if (autoplayWanted && !reduced.matches) {
      pauseBtn = makeButton('pause', 'Pause banner rotation', ICON.pause);
      controls.appendChild(pauseBtn);
    }
    prevBtn = makeButton('prev', 'Previous banner', ICON.prev);
    nextBtn = makeButton('next', 'Next banner', ICON.next);
    controls.appendChild(prevBtn);
    controls.appendChild(nextBtn);
    stage.appendChild(controls);

    evictStrangers();

    on(prevBtn, 'click', function () {
      if (prevBtn.getAttribute('aria-disabled') === 'true') { return; }
      handedOver();
      goTo(index - 1);
    });
    on(nextBtn, 'click', function () {
      if (nextBtn.getAttribute('aria-disabled') === 'true') { return; }
      handedOver();
      goTo(index + 1);
    });
    if (pauseBtn) {
      on(pauseBtn, 'click', function () {
        userStopped = !userStopped;
        if (userStopped) {
          stopTimer();
        } else {
          // Pressing Play should start right away, even with the mouse still over it
          hovering = false;
          focusing = false;
          applyAutoplay();
        }
        syncPauseButton();
      });
    }

    on(viewport, 'scroll', function () {
      if (rafId) { return; }
      rafId = requestAnimationFrame(function () {
        rafId = 0;
        if (Date.now() < lockUntil) { return; }
        var i = nearestIndex();
        if (i !== index) { index = i; update(); }
      });
    }, { passive: true });

    on(viewport, 'pointerdown', handedOver);
    on(viewport, 'touchstart', handedOver, { passive: true });

    on(root, 'mouseenter', function () { hovering = true; stopTimer(); });
    on(root, 'mouseleave', function () { hovering = false; applyAutoplay(); });
    // Only keyboard focus pauses autoplay. A button that keeps focus after a
    // mouse click must not freeze the slideshow.
    on(root, 'focusin', function (e) {
      var keyboard = false;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = true; }
      if (keyboard) { focusing = true; stopTimer(); }
    });
    on(root, 'focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { focusing = false; applyAutoplay(); }
    });
    on(document, 'visibilitychange', applyAutoplay);

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        applyAutoplay();
      }, { threshold: 0.25 });
      io.observe(root);
    }

    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () { goTo(index, true); });
      ro.observe(viewport);
    }

    var mo = null;
    if ('MutationObserver' in window) {
      mo = new MutationObserver(function () {
        if (evictions > 8) { mo.disconnect(); return; } // something keeps re-adding it, leave it be
        evictStrangers();
      });
      mo.observe(track, { childList: true });
    }

    update();
    applyAutoplay();
    syncPauseButton();
    root.setAttribute('data-hp-ready', 'true');

    if (document.readyState === 'complete') {
      setTimeout(loadAllImages, 800);
    } else {
      on(window, 'load', function () { setTimeout(loadAllImages, 800); });
    }

    return {
      showBlock: function (el) {
        var i = slides.indexOf(el);
        if (i > -1) { goTo(i); }
      },
      destroy: function () {
        stopTimer();
        clearTimeout(syncTimer);
        if (rafId) { cancelAnimationFrame(rafId); }
        undo.forEach(function (fn) { fn(); });
        if (io) { io.disconnect(); }
        if (ro) { ro.disconnect(); }
        if (mo) { mo.disconnect(); }
        if (controls && controls.parentNode) { controls.parentNode.removeChild(controls); }
        root.removeAttribute('data-hp-ready');
      }
    };
  }

  function init(root) {
    if (instances.has(root)) { return; }
    var inst = Slideshow(root);
    if (inst) { instances.set(root, inst); }
  }

  function scan(scope) {
    if (scope.matches && scope.matches(ROOT)) { init(scope); }
    Array.prototype.forEach.call(scope.querySelectorAll(ROOT), init);
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll(ROOT), function (root) {
      var inst = instances.get(root);
      if (inst) { inst.destroy(); instances.delete(root); }
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest && e.target.closest(ROOT);
    var inst = root && instances.get(root);
    if (inst) { inst.showBlock(e.target); }
  });

  window.__homepureSlideshow = { scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
})();
